import { Injectable } from "@angular/core";

import { convertArrayBufferToBase64, DataMatrixMetadata, SignatureDimensionsCollection, DataMatrixScanResult } from "./utils";

@Injectable({
  providedIn: "root",
})
export class DataMatrixService {
  private dataMatrixPosition: any = null;
  private isSignatureExtractionSuccessful = false;

  // Dynamic values (calculated based on actual Data Matrix size)
  private signatureRectWidth = 0;
  private signatureRectHeight = 0;
  private signatureOffsetX = 0;
  private signatureOffsetY = 0;

  public async initializeZxing(): Promise<void> {
    try {
      // set the WASM path for zxing library
      const { prepareZXingModule } = await import("zxing-wasm/reader");
      prepareZXingModule({
        overrides: {
          locateFile: (path: string, prefix: string) => {
            if (path.endsWith(".wasm")) {
              return `assets/zxing-wasm/${path}`;
            }
            return prefix + path;
          },
        },
        equalityFn: Object.is,
        fireImmediately: false,
      });
    } catch (error) {
      console.warn("Failed to initialize zxing WASM module:", error);
    }
  }

  public stripOuterSingleQuotes(input: string): string {
    if (typeof input !== 'string') {
      return input;
    };

    return input.replace(/^'+|'+$/g, '');
  }

  public async scanForDataMatrix(file: File): Promise<DataMatrixScanResult> {
    let base64Data: string = "";

    try {
      // reset Data Matrix detection state
      this.dataMatrixPosition = null;
      this.isSignatureExtractionSuccessful = true;

      // scan for Data Matrix using zxing-wasm
      const { readBarcodes } = await import("zxing-wasm/reader");
      const results = await readBarcodes(file);

      if (results && results.length > 0) {
        for (const result of results) {
          if (result.format === "DataMatrix") {
            let id = "";
            let version = "";
            let width = 64;
            let height = 64;

            try {
              const data = JSON.parse(this.stripOuterSingleQuotes(result.text)).data;
              id = data.id;
              version = data.version;

              const { symbol } = result;
              width = symbol.width;
              height = symbol.height;
            } catch (e) {
              console.warn("Failed to parse Data Matrix JSON:", e);
            }

            // handle Data Matrix specifically for signature detection
            base64Data = await this.handleDataMatrixDetection(result, file, {
              id,
              version,
              height,
              width,
            });

            return {
              base64Data,
              hasDataMatrix: this.isSignatureExtractionSuccessful,
            };
          }
        }
      }
    } catch (error) {
      // barcode scanning failed, but continue with normal signature upload
      console.warn("No barcodes detected in the image");
    }

    return {
      base64Data:
        base64Data || convertArrayBufferToBase64(await file.arrayBuffer()),
      hasDataMatrix: false,
    };
  }

  private calculateSignatureDimensions({
    id,
    version,
    height,
    width,
  }: DataMatrixMetadata): void {
    // calculate the actual pixel dimensions of the Data Matrix
    const pixelWidth = Math.abs(
      this.dataMatrixPosition.topRight.x - this.dataMatrixPosition.topLeft.x
    );

    const pixelHeight = Math.abs(
      this.dataMatrixPosition.bottomLeft.y - this.dataMatrixPosition.topLeft.y
    );

    // calculate pixels per module
    const pixelsPerModuleX = pixelWidth / width;
    const pixelsPerModuleY = pixelHeight / height;
    const avgPixelsPerModule = (pixelsPerModuleX + pixelsPerModuleY) / 2;
    const signatureDimensions = SignatureDimensionsCollection[id][version];

    // calculate scale ratio based on Data Matrix size compared to baseline
    const sizeRatio =
      avgPixelsPerModule / signatureDimensions.baseLinePixelsPerModule;

    // update dynamic values based on Data Matrix size
    this.signatureRectWidth = Math.round(
      signatureDimensions.rectWidth * sizeRatio
    );

    this.signatureRectHeight = Math.round(
      signatureDimensions.rectHeight * sizeRatio
    );

    this.signatureOffsetX = Math.round(signatureDimensions.offsetX * sizeRatio);

    this.signatureOffsetY = Math.round(signatureDimensions.offsetY * sizeRatio);
  }

  private async createSignatureOverlay(file: File): Promise<string> {
    let base64Data: string = "";

    try {
      // create an image element from the file
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        console.error("Could not get canvas context");
        return base64Data;
      }

      base64Data = await new Promise((resolve, reject): any => {
        img.addEventListener("load", () =>
          this.handleImageLoad(img, canvas, ctx, resolve)
        );

        img.addEventListener("error", () =>
          reject(new Error("Failed to load image"))
        );

        // convert file to data URL
        const reader = new FileReader();

        reader.addEventListener("load", (e) =>
          this.handleFileReaderLoad(e, img)
        );

        reader.readAsDataURL(file);
      });

      return base64Data;
    } catch (error) {
      console.error("Error creating signature overlay:", error);
    }

    return base64Data;
  }

  private async handleDataMatrixDetection(
    result: any,
    file: File,
    signatureTemplateData: DataMatrixMetadata
  ): Promise<string> {
    let base64Data: string = "";

    try {
      // extract barcode position from corner points
      let cornerPoints;
      if (result.position?.cornerPoints) {
        cornerPoints = result.position.cornerPoints;
      } else if (result.position?.topLeft) {
        cornerPoints = [
          result.position.topLeft,
          result.position.topRight,
          result.position.bottomRight,
          result.position.bottomLeft,
        ];
      } else if (result.position && Array.isArray(result.position)) {
        cornerPoints = result.position;
      } else {
        this.failedSignatureExtraction(`Unknown Data Matrix position format: ${result.position}`);
        return base64Data;
      }

      if (!cornerPoints || cornerPoints.length < 4) {
        this.failedSignatureExtraction(`Insufficient corner points for Data Matrix positioning`);
        return base64Data;
      }

      this.dataMatrixPosition = {
        topLeft: {
          x: Math.round(cornerPoints[0].x),
          y: Math.round(cornerPoints[0].y),
        },
        topRight: {
          x: Math.round(cornerPoints[1].x),
          y: Math.round(cornerPoints[1].y),
        },
        bottomRight: {
          x: Math.round(cornerPoints[2].x),
          y: Math.round(cornerPoints[2].y),
        },
        bottomLeft: {
          x: Math.round(cornerPoints[3].x),
          y: Math.round(cornerPoints[3].y),
        },
      };

      // calculate scale based on Data Matrix dimensions
      this.calculateSignatureDimensions(signatureTemplateData);

      // create signature overlay
      base64Data = await this.createSignatureOverlay(file);

      return base64Data;
    } catch (error) {
      console.error("Error handling Data Matrix detection:", error);
    }

    return base64Data;
  }

  /**
   * Handles the FileReader load event to set the image source
   * @param e - The FileReader load event
   * @param img - The image element to set the source for
   */
  private handleFileReaderLoad(
    e: ProgressEvent<FileReader>,
    img: HTMLImageElement
  ): void {
    img.src = e.target?.result as string;
  }

  /**
   * Handles the image load event for signature cropping and processing
   * @param img - The loaded image element
   * @param canvas - The canvas element for image processing
   * @param ctx - The 2D rendering context
   * @param resolve - Promise resolve function
   */
  private handleImageLoad(
    img: HTMLImageElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    resolve: (value: unknown) => void
  ): void {
    let base64Data: string = "";

    // set canvas size to match image
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // draw the original image
    ctx.drawImage(img, 0, 0);

    // calculate signature rectangle position
    const barcodeTopLeft = this.dataMatrixPosition.topLeft;
    const rectX = barcodeTopLeft.x - this.signatureOffsetX;
    const rectY = barcodeTopLeft.y - this.signatureOffsetY;

    // ensure the crop area is within image bounds
    const cropX = Math.max(0, rectX);
    const cropY = Math.max(0, rectY);
    const cropWidth = Math.min(
      this.signatureRectWidth,
      img.naturalWidth - cropX
    );

    const cropHeight = Math.min(
      this.signatureRectHeight,
      img.naturalHeight - cropY
    );

    // extract the signature area using getImageData and create a new canvas
    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");

    if (croppedCtx && cropWidth > 0 && cropHeight > 0) {
      // set cropped canvas size
      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;

      // draw the cropped signature area
      croppedCtx.drawImage(
        img,
        // source rectangle (signature area)
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        // destination rectangle (full cropped canvas)
        0,
        0,
        cropWidth,
        cropHeight
      );

      // convert cropped canvas to base64 for signature preview
      base64Data = croppedCanvas.toDataURL("image/png").split(",")[1];
    } else {
      this.failedSignatureExtraction("Could not crop signature area - invalid dimensions or context")

      // fallback: use original image with overlay
      base64Data = canvas.toDataURL("image/png").split(",")[1];
    }

    // resolves back to selectedSignatureBase64
    resolve(base64Data);
  }

  private failedSignatureExtraction(message: string) {
    console.warn(message);
    this.isSignatureExtractionSuccessful = false;
  }
}
